import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Form, Image } from "react-bootstrap";
import Rating from "../components/Rating";
import { useDispatch, useSelector } from "react-redux";
import {
  listProductDetails,
  createProductReview,
} from "../actions/productActions";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { PRODUCT_CREATE_REVIEW_RESET } from "../constants/productConstans";
import { ipApi } from "../actions/userActions";

const formatReviewDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.substring(0, 10);
  }
};

const ProductScreen = () => {
  const navigate = useNavigate();
  let { id } = useParams();
  let [qty, setQty] = useState(1);
  let [rating, setRating] = useState(0);
  let [comment, setComment] = useState("");

  const dispatch = useDispatch();

  let [country, setCountry] = useState("");
  // ISO country code from ipapi.co (e.g. "MX"). null = lookup not finished
  // (or failed); we treat that as "assume Mexico" so we don't block legitimate
  // customers when a third-party geolocation service is rate-limited or blocked
  // by an ad blocker / VPN. The real gate against shipping abroad is the
  // shipping address form on /shipping.
  let [countryCode, setCountryCode] = useState(null);

  const productDetail = useSelector((state) => state.productDetail);
  const { loading, error, product } = productDetail;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const productCreateReview = useSelector((state) => state.productCreateReview);
  const { error: errorProductReview, success: successProductReview } =
    productCreateReview;

  useEffect(() => {
    const checkCountry = async () => {
      try {
        const data = await ipApi();
        setCountry(data.Country || "");
        setCountryCode(data.CountryCode ? data.CountryCode.toUpperCase() : null);
      } catch (error) {
        console.log(error);
      }
    };
    checkCountry();

    if (successProductReview) {
      setRating(0);
      setComment("");
      dispatch({ type: PRODUCT_CREATE_REVIEW_RESET });
    }

    dispatch(listProductDetails(id));
  }, [dispatch, id, successProductReview]);

  const addToCartHandler = () => {
    navigate(`/cart/${id}?qty=${qty}`);
  };

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(
      createProductReview(id, {
        rating,
        comment,
      })
    );
    setRating(0);
    setComment("");
  };

  // Build kicker chip text (e.g. "DAMA · PERFUMES" or "HADACY · COSMÉTICOS")
  const kickerParts = [];
  if (product?.genero) kickerParts.push(product.genero);
  if (product?.tipo) kickerParts.push(product.tipo);
  const kicker = kickerParts.join(" · ").toUpperCase();

  const inStock = product?.countInStock > 0;
  const priceNum = Number(product?.price) || 0;
  const total = (priceNum * Number(qty || 1)).toLocaleString("es-MX");
  const priceLabel = priceNum.toLocaleString("es-MX");
  const numReviews = product?.numReviews || 0;
  const avgRating = product?.rating || 0;
  // Don't try to render the details body until the product is fully loaded.
  // Redux can briefly hand us a hollow `product` between route changes, which
  // would otherwise crash on things like `product.price.toLocaleString()`.
  const productReady = Boolean(product && product._id);

  return (
    <>
      <style>{`
        .hadacy-product {
          --cream: #f5efe1;
          --dark: #2a1f0c;
          --gold: #b58c2c;
          --gold-light: #d4a949;
          --muted: #8b7458;
          --card-bg: #fbf7ec;
          --recess: #ede3cf;
          --hairline: rgba(43, 31, 12, 0.12);
          color: var(--dark);
        }
        .hadacy-product__back {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          text-decoration: none;
          padding: 10px 0;
          margin: 8px 0 24px;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .hadacy-product__back i { transition: transform 0.2s ease; }
        .hadacy-product__back:hover {
          color: var(--gold);
        }
        .hadacy-product__back:hover i {
          transform: translateX(-3px);
        }

        .hadacy-product__layout {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
          gap: 56px;
          align-items: start;
        }
        @media (max-width: 991px) {
          .hadacy-product__layout {
            grid-template-columns: 1fr;
            gap: 32px;
          }
        }

        /* ─── Image column ─── */
        .hadacy-product__media {
          position: sticky;
          top: 24px;
        }
        @media (max-width: 991px) {
          .hadacy-product__media { position: static; }
        }
        .hadacy-product__image-wrap {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          padding: 18px;
          border-radius: 4px;
          box-shadow: 0 2px 14px rgba(43, 31, 12, 0.06);
        }
        .hadacy-product__image-wrap img {
          width: 100%;
          height: auto;
          display: block;
        }
        .hadacy-product__media-note {
          margin-top: 12px;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          color: var(--muted);
          font-style: italic;
          line-height: 1.5;
        }

        /* ─── Details column ─── */
        .hadacy-product__chips {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          margin-bottom: 18px;
        }
        .hadacy-product__kicker {
          display: inline-block;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          padding: 5px 12px;
          border: 1px solid var(--gold);
          border-radius: 999px;
          background: transparent;
        }
        .hadacy-product__badge {
          display: inline-block;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--cream);
          padding: 5px 12px;
          border: 1px solid var(--dark);
          border-radius: 999px;
          background: var(--dark);
        }
        .hadacy-product__volume {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
          margin: -6px 0 18px;
        }
        .hadacy-product__name {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: clamp(34px, 5vw, 52px);
          line-height: 1.05;
          color: var(--dark);
          margin: 0 0 16px;
          letter-spacing: -0.01em;
        }
        .hadacy-product__rating {
          display: flex;
          align-items: center;
          gap: 14px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--muted);
          margin-bottom: 18px;
        }
        .hadacy-product__rating .rating { display: inline-flex; gap: 2px; }
        .hadacy-product__rating .rating br { display: none; }
        .hadacy-product__rating-text {
          letter-spacing: 0.04em;
        }
        .hadacy-product__price {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 32px;
          color: var(--gold);
          margin-bottom: 24px;
          letter-spacing: 0.01em;
        }
        .hadacy-product__price-currency {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--muted);
          letter-spacing: 0.16em;
          margin-left: 8px;
          vertical-align: middle;
        }
        .hadacy-product__rule {
          height: 1px;
          background: var(--hairline);
          margin: 0 0 24px;
          border: 0;
        }
        .hadacy-product__rule--gold {
          width: 48px;
          height: 1px;
          background: var(--gold);
          border: 0;
          margin: 0 0 12px;
        }
        .hadacy-product__description {
          font-family: 'Jost', sans-serif;
          font-size: 15px;
          line-height: 1.7;
          color: var(--dark);
          margin-bottom: 18px;
        }
        .hadacy-product__inspo {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-style: italic;
          color: var(--gold);
          margin: 0 0 28px;
          padding: 4px 0 4px 14px;
          border-left: 2px solid var(--gold);
          line-height: 1.6;
        }
        .hadacy-product__inspo em {
          font-weight: 600;
          font-style: italic;
        }

        /* ─── Section heading helper (notes, meta) ─── */
        .hadacy-product__section-title {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--dark);
          margin: 0 0 4px;
        }
        .hadacy-product__section-rule {
          width: 32px;
          height: 1px;
          background: var(--gold);
          border: 0;
          margin: 0 0 14px;
        }

        /* ─── Notes chips (perfume) ─── */
        .hadacy-product__notes {
          margin: 0 0 28px;
        }
        .hadacy-product__notes-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .hadacy-product__notes-chip {
          display: inline-block;
          font-family: 'Cormorant Garamond', serif;
          font-size: 17px;
          font-weight: 500;
          letter-spacing: 0.01em;
          color: var(--gold);
          padding: 5px 18px;
          border: 1px solid var(--gold);
          border-radius: 999px;
          background: transparent;
        }

        /* ─── Meta strip (profile, ideal for) ─── */
        .hadacy-product__meta {
          margin: 0 0 28px;
          border-top: 1px solid var(--hairline);
        }
        .hadacy-product__meta-row {
          display: grid;
          grid-template-columns: 170px 1fr;
          gap: 16px;
          padding: 14px 0;
          border-bottom: 1px solid var(--hairline);
          font-family: 'Jost', sans-serif;
          font-size: 14px;
        }
        .hadacy-product__meta-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
          align-self: center;
        }
        .hadacy-product__meta-value {
          color: var(--dark);
          line-height: 1.5;
        }
        @media (max-width: 480px) {
          .hadacy-product__meta-row {
            grid-template-columns: 1fr;
            gap: 4px;
          }
        }

        /* ─── Detail accordions ─── */
        .hadacy-product__detail {
          border-top: 1px solid var(--hairline);
        }
        .hadacy-product__detail:last-of-type {
          border-bottom: 1px solid var(--hairline);
          margin-bottom: 28px;
        }
        .hadacy-product__detail summary {
          list-style: none;
          cursor: pointer;
          padding: 16px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--dark);
        }
        .hadacy-product__detail summary::-webkit-details-marker { display: none; }
        .hadacy-product__detail summary::after {
          content: "+";
          font-family: 'Jost', sans-serif;
          font-size: 18px;
          font-weight: 300;
          color: var(--gold);
          transition: transform 0.2s ease;
        }
        .hadacy-product__detail[open] summary::after {
          content: "−";
        }
        .hadacy-product__detail-body {
          padding: 0 0 18px;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          line-height: 1.7;
          color: var(--muted);
        }

        /* ─── Purchase card ─── */
        .hadacy-product__buy {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 6px;
          padding: 24px 24px 22px;
          box-shadow: 0 2px 14px rgba(43, 31, 12, 0.06);
        }
        .hadacy-product__buy-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 0;
          border-top: 1px solid var(--hairline);
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--dark);
        }
        .hadacy-product__buy-row:first-child { border-top: 0; padding-top: 0; }
        .hadacy-product__buy-label {
          color: var(--muted);
          letter-spacing: 0.06em;
        }
        .hadacy-product__buy-value {
          font-weight: 500;
          color: var(--dark);
        }
        .hadacy-product__stock {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 600;
        }
        .hadacy-product__stock--in {
          background: rgba(181, 140, 44, 0.12);
          color: var(--gold);
        }
        .hadacy-product__stock--out {
          background: rgba(150, 60, 60, 0.10);
          color: #963c3c;
        }
        .hadacy-product__qty-select {
          width: 90px;
          padding: 8px 36px 8px 16px;
          border-radius: 999px;
          border: 1px solid var(--gold);
          background-color: transparent;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='%23b58c2c' d='M4 6l4 4 4-4z'/></svg>");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 14px;
          color: var(--gold);
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          font-weight: 500;
          appearance: none;
          -webkit-appearance: none;
          cursor: pointer;
        }
        .hadacy-product__qty-select:focus {
          outline: 0;
          box-shadow: 0 0 0 3px rgba(181, 140, 44, 0.18);
        }
        .hadacy-product__total {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          color: var(--gold);
          font-weight: 500;
        }
        .hadacy-product__shipping-note {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          color: var(--muted);
          line-height: 1.6;
          margin-top: 4px;
        }
        .hadacy-product__cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          margin-top: 18px;
          padding: 16px 24px;
          background: var(--dark);
          color: var(--cream);
          border: 1px solid var(--dark);
          border-radius: 999px;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease,
            border-color 0.2s ease, transform 0.2s ease;
        }
        .hadacy-product__cta:hover:not(:disabled) {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--cream);
        }
        .hadacy-product__cta:hover:not(:disabled) i {
          transform: translateX(4px);
        }
        .hadacy-product__cta:disabled {
          background: var(--recess);
          color: var(--muted);
          border-color: var(--hairline);
          cursor: not-allowed;
          letter-spacing: 0.16em;
        }
        .hadacy-product__cta i { transition: transform 0.2s ease; }
        .hadacy-product__cta-warn {
          display: block;
          margin-top: 10px;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          color: var(--muted);
          line-height: 1.5;
          text-align: center;
        }

        /* ─── Reviews section ─── */
        .hadacy-product-reviews {
          margin-top: 72px;
          padding-top: 40px;
          border-top: 1px solid var(--hairline);
        }
        .hadacy-product-reviews__layout {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
          gap: 56px;
          align-items: start;
        }
        @media (max-width: 991px) {
          .hadacy-product-reviews__layout {
            grid-template-columns: 1fr;
            gap: 36px;
          }
        }
        .hadacy-product-reviews__title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 36px;
          color: var(--dark);
          margin: 0 0 12px;
          letter-spacing: -0.005em;
        }
        .hadacy-product-reviews__sub {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          color: var(--muted);
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 28px;
        }
        .hadacy-product-reviews__empty {
          padding: 28px 24px;
          text-align: center;
          background: var(--recess);
          border: 1px dashed var(--hairline);
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--muted);
          font-style: italic;
        }
        .hadacy-product-review {
          padding: 20px 0;
          border-bottom: 1px solid var(--hairline);
        }
        .hadacy-product-review:last-child { border-bottom: 0; }
        .hadacy-product-review__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
          gap: 12px;
        }
        .hadacy-product-review__name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 20px;
          font-weight: 500;
          color: var(--dark);
        }
        .hadacy-product-review__date {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          color: var(--muted);
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }
        .hadacy-product-review__stars { margin-bottom: 8px; }
        .hadacy-product-review__stars .rating br { display: none; }
        .hadacy-product-review__comment {
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          line-height: 1.7;
          color: var(--dark);
          margin: 0;
        }

        /* ─── Review form ─── */
        .hadacy-product-review-form {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 6px;
          padding: 28px 26px;
          box-shadow: 0 2px 14px rgba(43, 31, 12, 0.06);
        }
        .hadacy-product-review-form__title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 26px;
          color: var(--dark);
          margin: 0 0 4px;
          letter-spacing: -0.005em;
        }
        .hadacy-product-review-form__lead {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--muted);
          margin: 0 0 18px;
          line-height: 1.5;
        }
        .hadacy-product-review-form .form-label {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: var(--muted);
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .hadacy-product-review-form .form-control,
        .hadacy-product-review-form .form-select {
          background: var(--cream);
          border: 1px solid var(--hairline);
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--dark);
          padding: 10px 14px;
        }
        .hadacy-product-review-form .form-control:focus,
        .hadacy-product-review-form .form-select:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px rgba(181, 140, 44, 0.15);
          background: var(--card-bg);
        }
        .hadacy-product-review-form textarea.form-control {
          min-height: 110px;
          resize: vertical;
        }
        .hadacy-product-review-form__cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-top: 18px;
          padding: 12px 28px;
          background: var(--dark);
          color: var(--cream);
          border: 1px solid var(--dark);
          border-radius: 999px;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease,
            border-color 0.2s ease;
        }
        .hadacy-product-review-form__cta:hover {
          background: var(--gold);
          border-color: var(--gold);
        }
        .hadacy-product-review-form__login-prompt {
          padding: 18px 20px;
          background: var(--recess);
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--muted);
          text-align: center;
          line-height: 1.6;
        }
        .hadacy-product-review-form__login-prompt a {
          color: var(--gold);
          font-weight: 600;
          text-decoration: none;
          border-bottom: 1px solid var(--gold);
        }
        .hadacy-product-review-form__login-prompt a:hover { color: var(--gold-light); }

        /* ─── Inline banners ─── */
        .hadacy-product-banner {
          padding: 12px 16px;
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          line-height: 1.5;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .hadacy-product-banner--success {
          background: rgba(181, 140, 44, 0.10);
          color: var(--gold);
          border: 1px solid rgba(181, 140, 44, 0.25);
        }
        .hadacy-product-banner--danger {
          background: rgba(150, 60, 60, 0.08);
          color: #963c3c;
          border: 1px solid rgba(150, 60, 60, 0.22);
        }
      `}</style>

      <div className="hadacy-product">
        <Link className="hadacy-product__back" to="/">
          <i className="fas fa-arrow-left"></i> Volver
        </Link>

        {error ? (
          <Message variant="danger">{error}</Message>
        ) : loading || !productReady ? (
          <Loader></Loader>
        ) : (
          <>
            <div className="hadacy-product__layout">
              {/* ── Media column ── */}
              <div className="hadacy-product__media">
                <div className="hadacy-product__image-wrap">
                  <Image src={product.image} alt={product.name} fluid />
                </div>
                <p className="hadacy-product__media-note">
                  * Imágenes de referencia, el producto puede variar ligeramente
                  en su presentación y color.
                </p>
              </div>

              {/* ── Details column ── */}
              <div className="hadacy-product__details">
                {(kicker || product.badge) && (
                  <div className="hadacy-product__chips">
                    {kicker && (
                      <span className="hadacy-product__kicker">{kicker}</span>
                    )}
                    {product.badge && (
                      <span className="hadacy-product__badge">
                        {product.badge}
                      </span>
                    )}
                  </div>
                )}
                <h1 className="hadacy-product__name">{product.name}</h1>

                {product.volume && (
                  <div className="hadacy-product__volume">
                    {product.volume}
                  </div>
                )}

                <div className="hadacy-product__rating">
                  <Rating value={avgRating} text="" />
                  <span className="hadacy-product__rating-text">
                    {numReviews === 0
                      ? "Aún no hay reseñas"
                      : `${numReviews} ${
                          numReviews === 1 ? "reseña" : "reseñas"
                        }`}
                  </span>
                </div>

                <div className="hadacy-product__price">
                  ${priceLabel}
                  <span className="hadacy-product__price-currency">MXN</span>
                </div>

                <hr className="hadacy-product__rule" />

                {product.description && (
                  <p className="hadacy-product__description">
                    {product.description}
                  </p>
                )}

                {(product.inspo || product.brand) && (
                  <p className="hadacy-product__inspo">
                    {product.inspo ? (
                      <>
                        Inspirado en <em>{product.inspo}</em>
                        {product.brand && <> · {product.brand}</>}
                      </>
                    ) : (
                      <em>{product.brand}</em>
                    )}
                  </p>
                )}

                {Array.isArray(product.notes) && product.notes.length > 0 && (
                  <div className="hadacy-product__notes">
                    <h3 className="hadacy-product__section-title">
                      Notas olfativas
                    </h3>
                    <hr className="hadacy-product__section-rule" />
                    <div className="hadacy-product__notes-chips">
                      {product.notes.map((note, i) => (
                        <span
                          key={`${note}-${i}`}
                          className="hadacy-product__notes-chip"
                        >
                          {note}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(product.profile || product.idealFor) && (
                  <div className="hadacy-product__meta">
                    {product.profile && (
                      <div className="hadacy-product__meta-row">
                        <span className="hadacy-product__meta-label">
                          Perfil olfativo
                        </span>
                        <span className="hadacy-product__meta-value">
                          {product.profile}
                        </span>
                      </div>
                    )}
                    {product.idealFor && (
                      <div className="hadacy-product__meta-row">
                        <span className="hadacy-product__meta-label">
                          Ideal para
                        </span>
                        <span className="hadacy-product__meta-value">
                          {product.idealFor}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {product.ingredients && (
                  <details className="hadacy-product__detail">
                    <summary>Ingredientes</summary>
                    <div className="hadacy-product__detail-body">
                      {product.ingredients}
                    </div>
                  </details>
                )}

                {product.modeOfUse && (
                  <details className="hadacy-product__detail" open>
                    <summary>Modo de uso</summary>
                    <div className="hadacy-product__detail-body">
                      {product.modeOfUse}
                    </div>
                  </details>
                )}

                {/* ── Purchase card ── */}
                <div className="hadacy-product__buy">
                  <div className="hadacy-product__buy-row">
                    <span className="hadacy-product__buy-label">Estatus</span>
                    <span
                      className={`hadacy-product__stock ${
                        inStock
                          ? "hadacy-product__stock--in"
                          : "hadacy-product__stock--out"
                      }`}
                    >
                      <i
                        className={
                          inStock ? "fas fa-check" : "fas fa-times"
                        }
                      ></i>
                      {inStock ? "En stock" : "Agotado"}
                    </span>
                  </div>

                  {inStock && (
                    <div className="hadacy-product__buy-row">
                      <span className="hadacy-product__buy-label">
                        Cantidad
                      </span>
                      <Form.Select
                        className="hadacy-product__qty-select"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                      >
                        {[...Array(product.countInStock).keys()].map((x) => (
                          <option key={x + 1} value={x + 1}>
                            {x + 1}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  )}

                  <div className="hadacy-product__buy-row">
                    <span className="hadacy-product__buy-label">Subtotal</span>
                    <span className="hadacy-product__total">
                      ${total}
                      <span className="hadacy-product__price-currency">
                        MXN
                      </span>
                    </span>
                  </div>

                  <div className="hadacy-product__buy-row">
                    <span className="hadacy-product__buy-label">Envío</span>
                    <span style={{ textAlign: "right" }}>
                      <span className="hadacy-product__buy-value">
                        $150 MXN
                      </span>
                      <span className="hadacy-product__shipping-note">
                        Tarifa fija a toda la República.
                        <br />
                        Gratis en compras mayores a $1,500 MXN.
                      </span>
                    </span>
                  </div>

                  {userInfo && (() => {
                    const normalizedName = (country || "").trim().toLowerCase();
                    const isMexico =
                      countryCode === null
                        ? normalizedName === ""
                          ? true
                          : ["mexico", "méxico"].includes(normalizedName)
                        : countryCode === "MX";

                    const outOfStock = product.countInStock <= 0;
                    return (
                      <>
                        <button
                          onClick={addToCartHandler}
                          className="hadacy-product__cta"
                          type="button"
                          disabled={outOfStock || !isMexico}
                        >
                          {isMexico
                            ? outOfStock
                              ? "Producto agotado"
                              : "Agregar al carrito"
                            : "Solo enviamos dentro de México"}
                          {isMexico && !outOfStock && (
                            <i className="fas fa-arrow-right"></i>
                          )}
                        </button>
                        {!isMexico && (
                          <small className="hadacy-product__cta-warn">
                            Detectamos que te encuentras fuera de México. Por
                            el momento solo realizamos envíos dentro del país.
                            Si quieres comprar desde otro lugar, por favor
                            contáctanos.
                          </small>
                        )}
                      </>
                    );
                  })()}

                  {!userInfo && (
                    <Link
                      to="/login"
                      className="hadacy-product__cta"
                      style={{ textDecoration: "none" }}
                    >
                      Inicia sesión para comprar
                      <i className="fas fa-arrow-right"></i>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* ── Reviews ── */}
            <div className="hadacy-product-reviews">
              <div className="hadacy-product-reviews__layout">
                <div>
                  <h2 className="hadacy-product-reviews__title">Reseñas</h2>
                  <hr className="hadacy-product__rule--gold" />
                  <div className="hadacy-product-reviews__sub">
                    {numReviews === 0
                      ? "Sé la primera persona en compartir tu experiencia"
                      : `${numReviews} ${
                          numReviews === 1 ? "opinión" : "opiniones"
                        } de nuestra comunidad`}
                  </div>

                  {product.reviews.length === 0 ? (
                    <div className="hadacy-product-reviews__empty">
                      Aún no hay reseñas para este producto.
                    </div>
                  ) : (
                    <div>
                      {product.reviews.map((review) => (
                        <div
                          className="hadacy-product-review"
                          key={review._id}
                        >
                          <div className="hadacy-product-review__head">
                            <span className="hadacy-product-review__name">
                              {review.name}
                            </span>
                            <span className="hadacy-product-review__date">
                              {formatReviewDate(review.createdAt)}
                            </span>
                          </div>
                          <div className="hadacy-product-review__stars">
                            <Rating value={review.rating} text="" />
                          </div>
                          <p className="hadacy-product-review__comment">
                            {review.comment}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="hadacy-product-review-form">
                    <h3 className="hadacy-product-review-form__title">
                      Escribe una reseña
                    </h3>
                    <p className="hadacy-product-review-form__lead">
                      Tu opinión ayuda a otras personas a descubrir nuestros
                      productos.
                    </p>

                    {errorProductReview && (
                      <div className="hadacy-product-banner hadacy-product-banner--danger">
                        <i className="fas fa-exclamation-circle"></i>
                        {errorProductReview}
                      </div>
                    )}
                    {successProductReview && (
                      <div className="hadacy-product-banner hadacy-product-banner--success">
                        <i className="fas fa-check-circle"></i>
                        Reseña enviada correctamente.
                      </div>
                    )}

                    {userInfo ? (
                      <Form onSubmit={submitHandler}>
                        <Form.Group controlId="rating" className="mb-3">
                          <Form.Label>Calificación</Form.Label>
                          <Form.Select
                            value={rating}
                            onChange={(e) => setRating(e.target.value)}
                          >
                            <option value="">Selecciona…</option>
                            <option value="1">1 — Malo</option>
                            <option value="2">2 — Regular</option>
                            <option value="3">3 — Bueno</option>
                            <option value="4">4 — Muy bueno</option>
                            <option value="5">5 — Excelente</option>
                          </Form.Select>
                        </Form.Group>

                        <Form.Group controlId="comment" className="mb-2">
                          <Form.Label>Comentario</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Cuéntanos qué te pareció…"
                          />
                        </Form.Group>

                        <button
                          type="submit"
                          className="hadacy-product-review-form__cta"
                        >
                          Enviar reseña
                          <i className="fas fa-arrow-right"></i>
                        </button>
                      </Form>
                    ) : (
                      <div className="hadacy-product-review-form__login-prompt">
                        <Link to="/login">Inicia sesión</Link> para escribir una
                        reseña.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ProductScreen;
